import React, { type ReactNode } from 'react'
import { NavLink, useNavigate } from 'react-router-dom'
import {
  Box,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  Icon,
  IconButton,
  Text,
  useColorModeValue,
  useDisclosure,
  VStack
} from '@chakra-ui/react'
import { RiDashboardLine, RiFileList3Line, RiLogoutBoxRLine, RiMenuLine, RiShieldUserLine, RiUserLine, RiKey2Line, RiForbid2Line, RiGiftLine, RiCake3Line, RiVipCrownLine } from 'react-icons/ri'

interface AdminLayoutProps {
  children: ReactNode
}

interface NavItem {
  to: string
  label: string
  icon: React.ElementType
}

const NAV_ITEMS: NavItem[] = [
  { to: '/admin/dashboard', label: 'Dashboard', icon: RiDashboardLine },
  { to: '/admin/tokens', label: 'Tokens', icon: RiKey2Line },
  { to: '/admin/exports', label: 'Exportacións', icon: RiFileList3Line },
  { to: '/admin/registrations', label: 'Rexistros', icon: RiUserLine },
  { to: '/admin/christmas-dinner', label: 'Cea Nadal', icon: RiCake3Line },
  { to: '/admin/votes', label: 'Votación', icon: RiVipCrownLine },
  { to: '/admin/blacklist', label: 'Lista Negra', icon: RiForbid2Line },
  { to: '/admin/free-list', label: 'Gratuítos', icon: RiGiftLine }
]

const SESSION_STORAGE_KEY = 'isAdminAuthenticated'

const AdminLayout = ({ children }: AdminLayoutProps): JSX.Element => {
  const navigate = useNavigate()
  const { isOpen, onOpen, onClose } = useDisclosure()

  const bg = useColorModeValue('gray.800', 'gray.900')
  const color = useColorModeValue('white', 'gray.200')
  const activeLinkColor = useColorModeValue('purple.300', 'purple.500')
  const activeLinkBg = useColorModeValue('whiteAlpha.200', 'blackAlpha.300')
  const drawerBg = useColorModeValue('gray.800', 'gray.900')

  const handleLogout = (): void => {
    sessionStorage.removeItem(SESSION_STORAGE_KEY)
    navigate('/admin/tokens')
    window.location.reload()
  }

  const inlineLinkStyle = {
    padding: '8px 14px',
    borderRadius: '6px',
    textDecoration: 'none',
    fontSize: '14px',
    whiteSpace: 'nowrap' as const
  }

  const activeInlineLinkStyle = {
    fontWeight: 'bold',
    color: activeLinkColor,
    backgroundColor: activeLinkBg
  }

  return (
    <Box>
      <Flex
        as="header"
        bg={bg}
        color={color}
        px={{ base: 4, md: 6 }}
        py={3}
        align="center"
        justify="space-between"
        position="sticky"
        top={0}
        zIndex="sticky"
        boxShadow="md"
        minH="56px"
      >
        {/* Logo + desktop nav */}
        <Flex align="center" gap={{ base: 3, md: 5 }} overflow="hidden">
          <Flex align="center" gap={2} flexShrink={0}>
            <Icon as={RiShieldUserLine} boxSize={5} color="purple.400" />
            <Text fontSize="lg" fontWeight="bold">Admin</Text>
          </Flex>

          {/* Desktop nav — hidden on mobile */}
          <Flex as="nav" gap={1} display={{ base: 'none', lg: 'flex' }}>
            {NAV_ITEMS.map(item => (
              <NavLink
                key={item.to}
                to={item.to}
                style={({ isActive }) =>
                  isActive
                    ? { ...inlineLinkStyle, ...activeInlineLinkStyle }
                    : inlineLinkStyle
                }
              >
                {item.label}
              </NavLink>
            ))}
          </Flex>
        </Flex>

        {/* Right side actions */}
        <Flex align="center" gap={2} flexShrink={0}>
          {/* Logout — icon only on mobile, icon+text on desktop */}
          <IconButton
            aria-label="Pechar sesión"
            icon={<RiLogoutBoxRLine />}
            colorScheme="purple"
            variant="outline"
            size="sm"
            display={{ base: 'flex', md: 'none' }}
            onClick={handleLogout}
          />
          <Flex
            as="button"
            display={{ base: 'none', md: 'flex' }}
            align="center"
            gap={2}
            px={3}
            py={1.5}
            borderRadius="md"
            border="1px solid"
            borderColor="purple.400"
            color="purple.300"
            fontSize="sm"
            cursor="pointer"
            _hover={{ bg: 'whiteAlpha.100' }}
            onClick={handleLogout}
          >
            <Icon as={RiLogoutBoxRLine} />
            <Text>Pechar Sesión</Text>
          </Flex>

          {/* Hamburger — visible only below lg */}
          <IconButton
            aria-label="Abrir menú"
            icon={<RiMenuLine />}
            variant="ghost"
            color="white"
            size="sm"
            display={{ base: 'flex', lg: 'none' }}
            onClick={onOpen}
          />
        </Flex>
      </Flex>

      {/* Mobile Drawer */}
      <Drawer isOpen={isOpen} placement="left" onClose={onClose}>
        <DrawerOverlay />
        <DrawerContent bg={drawerBg} color={color} maxW="260px">
          <DrawerCloseButton color="white" />
          <DrawerHeader borderBottomWidth="1px" borderColor="whiteAlpha.200">
            <Flex align="center" gap={2}>
              <Icon as={RiShieldUserLine} color="purple.400" />
              <Text>Admin</Text>
            </Flex>
          </DrawerHeader>
          <DrawerBody pt={4} px={3}>
            <VStack spacing={1} align="stretch">
              {NAV_ITEMS.map(item => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={onClose}
                  style={({ isActive }) => ({
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '10px 12px',
                    borderRadius: '8px',
                    textDecoration: 'none',
                    fontWeight: isActive ? 'bold' : 'normal',
                    color: isActive ? activeLinkColor : color,
                    backgroundColor: isActive ? activeLinkBg : 'transparent'
                  })}
                >
                  <Icon as={item.icon} boxSize={4} />
                  {item.label}
                </NavLink>
              ))}
            </VStack>
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      <Box as="main">
        {children}
      </Box>
    </Box>
  )
}

export default AdminLayout
